import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import Select from 'react-select'
import * as XLSX from 'xlsx'
import { useAuth } from '../../context/auth-context'
import { useOutletContext } from 'react-router-dom'
import { toast } from 'react-toastify'
import { 
  FaPlus, 
  FaEdit, 
  FaFileInvoiceDollar, 
  FaSpinner,
  FaSearch,
  FaFilter,
  FaDownload,
  FaUpload,
  FaStore,
  FaDollarSign,
  FaChartBar,
  FaTimes,
  FaCheckCircle
} from 'react-icons/fa'
import Modal from '../../components/Modal'
import { customStyles } from '../../styles'
import { formatLocalDateISO, formatToZMW, rootUrl } from '../../helpers'

const BUSINESS_TYPES = [
  'Grocery',
  'Bar',
  'Barbershop',
  'Bottle Store',
  'Salon',
  'Hardware',
  'Wholesale',
  'Tailoring',
  'Butchery',
  'Restaurant',
  'Pharmacy',
  'General Dealer',
  'Other'
]

const normalizeBusinessType = (value) => {
  if (!value) return 'Other'
  const lower = String(value).toLowerCase().trim()
  const match = BUSINESS_TYPES.find(
    (type) => type.toLowerCase() === lower
  )
  return match || 'Other'
}

const defaultShopForm = {
  trading_name: '',
  business_type: BUSINESS_TYPES[0],
  owner_id: '',
  shop_number: '',
  location: '',
  address: '',
  monthly_rent: '',
  notes: '',
  status: 1
}

const defaultBillingForm = {
  amount: '',
  description: '',
  billing_period: '',
  bill_date: formatLocalDateISO(),
  due_date: '',
  cc_id: '',
  coa_id: '',
}

function Shops() {
  const { user } = useAuth()
  const { allData } = useOutletContext()
  const [shops, setShops] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [shopModalOpen, setShopModalOpen] = useState(false)
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [shopForm, setShopForm] = useState(defaultShopForm)
  const [billingForm, setBillingForm] = useState(defaultBillingForm)
  const [savingShop, setSavingShop] = useState(false)
  const [savingBill, setSavingBill] = useState(false)
  const [editingShop, setEditingShop] = useState(null)
  const [selectedShop, setSelectedShop] = useState(null)
  const [importRows, setImportRows] = useState([])
  const [importing, setImporting] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [selectedFileName, setSelectedFileName] = useState('')

  const ownerOptions = useMemo(() => {
    const options = []
    const seen = new Set()

    if (Array.isArray(allData?.suppliers)) {
      allData.suppliers.forEach((cust) => {
        const value = cust.u_id ?? cust.id
        if (!value || seen.has(String(value))) return
        const nameParts = [cust.firstname, cust.lastname].filter(Boolean)
        const label = nameParts.length > 0 ? nameParts.join(' ') : (cust.name || cust.account_no || `Customer ${value}`)
        options.push({ value: String(value), label })
        seen.add(String(value))
      })
    }

    if (Array.isArray(allData?.suppliers)) {
      allData.suppliers
        .filter((item) => item.type === 3)
        .forEach((cust) => {
          const value = cust.id
          if (!value || seen.has(String(value))) return
          options.push({ value: String(value), label: cust.name || cust.person || `Customer ${value}` })
          seen.add(String(value))
        })
    }

    return options
  }, [allData?.customer, allData?.suppliers])

  const ownerName = (ownerId) => {
    if (!ownerId) return 'Not set'
    const option = ownerOptions.find((opt) => opt.value === String(ownerId))
    return option?.label || 'Not set'
  }

  const fetchShops = async () => {
    try {
      setLoading(true)
      const response = await axios.get(rootUrl(`/getShops/${user.id}`))
      setShops(response.data.data || [])
    } catch (error) {
      console.error(error)
      toast.error('Failed to load shops')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchShops()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const normalizeShopRow = (row) => {
    const getFirst = (keys) => {
      for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && String(row[key]).trim() !== '') {
          return String(row[key]).trim()
        }
      }
      return ''
    }

    const trading_name = getFirst(['NAME OF PREMISES (trading name)', 'NAME OF PREMISES', 'TRADING NAME', 'trading_name'])
    const business_type = getFirst(['TYPE OF BUSINESS', 'Business Type', 'TYPE', 'business_type'])
    const owner_name = getFirst(['NAME OF OWNER', 'Owner', 'OWNER', 'NAME'])
    const location = getFirst(['ADRESS / LOCATION', 'ADDRESS / LOCATION', 'LOCATION', 'Address'])
    const nrc = getFirst(['NRC NUMBER', 'NRC', 'ID NUMBER'])
    const phone = getFirst(['PHONE NUMBER', 'PHONE', 'CONTACT'])
    const amountPaidRaw = getFirst(['Amount paid', 'AMOUNT PAID', 'RENT', 'MONTHLY RENT'])
    const shop_number = getFirst(['SHOP NUMBER', 'Shop Number', 'SHOP NO', 'SHOP'])

    if (!trading_name || !shop_number || !owner_name) {
      return null
    }

    let monthly_rent = 0
    if (amountPaidRaw) {
      const normalized = amountPaidRaw.replace(/,/g, '')
      const parsed = parseFloat(normalized)
      if (!Number.isNaN(parsed)) {
        monthly_rent = parsed
      }
    }

    return {
      trading_name,
      business_type: normalizeBusinessType(business_type),
      owner_name,
      location,
      address: location,
      nrc,
      phone,
      monthly_rent,
      shop_number
    }
  }

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0]
    if (!file) {
      setImportRows([])
      setSelectedFileName('')
      return
    }

    setSelectedFileName(file.name)

    try {
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]
      const raw = XLSX.utils.sheet_to_json(worksheet, { defval: '' })

      const normalized = raw
        .map(normalizeShopRow)
        .filter((row) => row !== null)

      if (normalized.length === 0) {
        toast.error('No valid rows found in the uploaded file')
        setImportRows([])
      } else {
        setImportRows(normalized)
      }
    } catch (error) {
      console.error(error)
      toast.error('Failed to read Excel / CSV file')
      setImportRows([])
      setSelectedFileName('')
    }
  }

  const openImportModal = () => {
    setImportModalOpen(true)
    setImportRows([])
    setSelectedFileName('')
  }

  const closeImportModal = () => {
    setImportModalOpen(false)
    setImportRows([])
    setSelectedFileName('')
    // Reset file input
    const fileInput = document.getElementById('import-file-input')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const downloadSampleExcel = () => {
    const sample = [{
      'NAME OF PREMISES (trading name)': 'Lukama shop',
      'TYPE OF BUSINESS': 'Grocery',
      'NAME OF OWNER': 'Christine Lukama',
      'ADRESS / LOCATION': 'Kasapa market',
      'NRC NUMBER': '266513/67/1',
      'PHONE NUMBER': '0777442545',
      'Amount paid': '4955',
      'SHOP NUMBER': 'KASGRO 01'
    }]

    const worksheet = XLSX.utils.json_to_sheet(sample, {
      header: [
        'NAME OF PREMISES (trading name)',
        'TYPE OF BUSINESS',
        'NAME OF OWNER',
        'ADRESS / LOCATION',
        'NRC NUMBER',
        'PHONE NUMBER',
        'Amount paid',
        'SHOP NUMBER'
      ]
    })
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shops')
    XLSX.writeFile(workbook, 'shops_sample.xlsx')
  }

  const filteredShops = useMemo(() => {
    return shops.filter((shop) => {
      const matchesSearch =
        shop.trading_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shop.shop_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ownerName(shop.owner_id).toLowerCase().includes(searchTerm.toLowerCase())

      const matchesType =
        typeFilter === 'all'
          ? true
          : String(shop.business_type || '')
              .toLowerCase()
              .trim() === String(typeFilter)
              .toLowerCase()
              .trim()

      return matchesSearch && matchesType
    })
  }, [shops, searchTerm, typeFilter])

  const totalRent = useMemo(() => {
    return shops.reduce((sum, shop) => sum + (parseFloat(shop.monthly_rent) || 0), 0)
  }, [shops])

  const typeBreakdown = useMemo(() => {
    const map = {}
    shops.forEach((shop) => {
      if (!shop.business_type) return
      map[shop.business_type] = (map[shop.business_type] || 0) + 1
    })
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
  }, [shops])

  const columns = useMemo(() => [
    {
      name: 'Shop #',
      selector: (row) => row.shop_number,
      sortable: true,
      minWidth: '120px'
    },
    {
      name: 'Trading Name',
      selector: (row) => row.trading_name,
      sortable: true,
      grow: 2
    },
    {
      name: 'Owner',
      selector: (row) => ownerName(row.owner_id),
      sortable: true,
      grow: 2
    },
    {
      name: 'Business Type',
      selector: (row) => row.business_type,
      sortable: true
    },
    {
      name: 'Location',
      selector: (row) => row.location || '-',
      sortable: true,
      grow: 1.5
    },
    {
      name: 'Annual Rate',
      selector: (row) => formatToZMW(parseFloat(row.monthly_rent) || 0),
      sortable: true,
      right: true,
      minWidth: '140px'
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => openBillingModal(row)}
            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
            title="Bill shop"
          >
            <FaFileInvoiceDollar className="text-sm" />
          </button>
          <button
            onClick={() => openEditShop(row)}
            className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            title="Edit shop"
          >
            <FaEdit className="text-sm" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: '180px'
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ownerOptions])

  const openAddShop = () => {
    setEditingShop(null)
    setShopForm(defaultShopForm)
    setShopModalOpen(true)
  }

  const openEditShop = (shop) => {
    setEditingShop(shop)
    setShopForm({
      trading_name: shop.trading_name || '',
      business_type: shop.business_type || BUSINESS_TYPES[0],
      owner_id: shop.owner_id ? String(shop.owner_id) : '',
      shop_number: shop.shop_number || '',
      location: shop.location || '',
      address: shop.address || '',
      monthly_rent: shop.monthly_rent ?? '',
      notes: shop.notes || '',
      status: shop.status ?? 1
    })
    setShopModalOpen(true)
  }

  const handleShopFormChange = (e) => {
    const { name, value } = e.target
    setShopForm((prev) => ({ ...prev, [name]: value }))
  }

  const submitShopForm = async (e) => {
    e.preventDefault()
    if (!shopForm.trading_name || !shopForm.owner_id || !shopForm.shop_number) {
      toast.error('Trading name, owner and shop number are required')
      return
    }
    try {
      setSavingShop(true)
      const payload = {
        ...shopForm,
        uid: user.id,
        owner_id: shopForm.owner_id,
        business_type: normalizeBusinessType(shopForm.business_type)
      }

      if (payload.monthly_rent !== '') {
        payload.monthly_rent = parseFloat(payload.monthly_rent)
      }

      if (editingShop) {
        payload.id = editingShop.id
        await axios.post(rootUrl('/updateShop'), payload)
        toast.success('Shop updated')
      } else {
        await axios.post(rootUrl('/addShop'), payload)
        toast.success('Shop added')
      }
      setShopModalOpen(false)
      setShopForm(defaultShopForm)
      fetchShops()
    } catch (error) {
      console.error(error)
      toast.error(editingShop ? 'Failed to update shop' : 'Failed to add shop')
    } finally {
      setSavingShop(false)
    }
  }

  const findOwnerIdByName = (name) => {
    if (!name) return null
    const target = name.toLowerCase().trim()
    const match = ownerOptions.find((opt) => opt.label.toLowerCase().trim() === target)
    return match ? match.value : null
  }

  const importShops = async () => {
    if (!importRows.length) {
      toast.error('No rows loaded to import')
      return
    }
    try {
      setImporting(true)
      let success = 0
      let failed = 0

      for (const row of importRows) {
        try {
          let ownerId = findOwnerIdByName(row.owner_name)

          if (!ownerId) {
            const customerPayload = {
              uid: user.id,
              name: row.owner_name,
              contactPerson: row.owner_name,
              phoneNumber: row.phone || '',
              email: '',
              address: row.address || '',
              type: 3
            }
            const resp = await axios.post(rootUrl('/addCustomer'), customerPayload)
            const suppliers = resp.data?.data || []
            const created = suppliers.find((s) => s.name === row.owner_name && s.type === 3)
            if (created) {
              ownerId = String(created.id)
            } else {
              // Fallback: re-fetch owner options and try again
              ownerId = findOwnerIdByName(row.owner_name)
            }
          }

          if (!ownerId) {
            failed++
            continue
          }

          const shopPayload = {
            uid: user.id,
            trading_name: row.trading_name,
            business_type: normalizeBusinessType(row.business_type),
            owner_id: ownerId,
            shop_number: row.shop_number,
            location: row.location,
            address: row.address,
            monthly_rent: row.monthly_rent,
            notes: row.nrc ? `Imported NRC: ${row.nrc}` : '',
            status: 1
          }

          await axios.post(rootUrl('/addShop'), shopPayload)
          success++
        } catch (err) {
          console.error('Import row failed', row, err)
          failed++
        }
      }

      fetchShops()
      closeImportModal()

      if (success && !failed) {
        toast.success(`Imported ${success} shops successfully`)
      } else if (success && failed) {
        toast.warn(`Imported ${success} shops, ${failed} rows failed`)
      } else {
        toast.error('All rows failed to import')
      }
    } finally {
      setImporting(false)
    }
  }

  const openBillingModal = (shop) => {
    setSelectedShop(shop)
    setBillingForm({
      ...defaultBillingForm,
      amount: shop.monthly_rent ?? '',
      description: `Billing for ${shop.trading_name || 'shop'}`
    })
    setBillingModalOpen(true)
  }

  const handleBillingChange = (e) => {
    const { name, value } = e.target
    setBillingForm((prev) => ({ ...prev, [name]: value }))
  }

  const submitBillingForm = async (e) => {
    e.preventDefault()
    if (!selectedShop) return
    const requiredFields = ['amount', 'description', 'cc_id', 'coa_id']
    const missing = requiredFields.find((field) => !billingForm[field])
    if (missing) {
      toast.error('Please complete all billing fields')
      return
    }
    const amountValue = parseFloat(billingForm.amount)
    if (Number.isNaN(amountValue) || amountValue <= 0) {
      toast.error('Amount must be greater than zero')
      return
    }
    try {
      setSavingBill(true)
      const payload = {
        uid: user.id,
        shop_id: selectedShop.id,
        amount: amountValue,
        description: billingForm.description,
        billing_period: billingForm.billing_period,
        bill_date: billingForm.bill_date,
        due_date: billingForm.due_date,
        bank_id: 1,
        sof_id: 1,
        cc_id: billingForm.cc_id,
        coa_id: billingForm.coa_id
      }
      await axios.post(rootUrl('/billShop'), payload)
      toast.success('Bill created successfully')
      fetchShops()
      setBillingModalOpen(false)
      setSelectedShop(null)
      setBillingForm(defaultBillingForm)
    } catch (error) {
      console.error(error)
      toast.error('Failed to create bill')
    } finally {
      setSavingBill(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-xl p-3">
              <FaStore className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Revenue Shops</h1>
              <p className="text-blue-100 text-sm mt-1">Manage trading spaces, owners and billing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadSampleExcel}
              className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium"
              title="Download Sample Excel"
            >
              <FaDownload />
              <span className="hidden sm:inline">Sample</span>
            </button>
            <button
              type="button"
              onClick={openImportModal}
              className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors text-sm font-medium"
              title="Upload Excel"
            >
              <FaUpload />
              <span className="hidden sm:inline">Import</span>
            </button>
            <button
              onClick={openAddShop}
              className="flex items-center gap-2 px-4 py-2.5 bg-white text-blue-950 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-sm"
            >
              <FaPlus />
              <span>Add Shop</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-100 rounded-lg p-2">
              <FaStore className="text-blue-600 text-xl" />
            </div>
            <div className="text-sm font-medium text-gray-600">Total Shops</div>
          </div>
          <div className="text-3xl font-bold text-gray-900">{shops.length}</div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-green-100 rounded-lg p-2">
              <FaDollarSign className="text-green-600 text-xl" />
            </div>
            <div className="text-sm font-medium text-gray-600">Avg. Monthly Rent</div>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {shops.length ? formatToZMW(totalRent / shops.length) : formatToZMW(0)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-purple-100 rounded-lg p-2">
              <FaChartBar className="text-purple-600 text-xl" />
            </div>
            <div className="text-sm font-medium text-gray-600">Top Categories</div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {typeBreakdown.length === 0 ? (
              <span className="text-gray-400 text-sm">No shops yet</span>
            ) : (
              typeBreakdown.map(([type, count]) => (
                <span key={type} className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  {type} ({count})
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FaSearch className="text-gray-500 text-sm" />
                <span>Search</span>
              </div>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by shop name, number or owner..."
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FaFilter className="text-gray-500 text-sm" />
                <span>Business Type</span>
              </div>
            </label>
            <select
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[180px]"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All business types</option>
              {BUSINESS_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredShops}
          progressPending={loading}
          pagination
          highlightOnHover
          striped
          customStyles={customStyles}
          persistTableHead
        />
      </div>

      {shopModalOpen && (
        <Modal
          title={editingShop ? 'Edit Shop' : 'Add Shop'}
          onClose={() => {
            setShopModalOpen(false)
            setEditingShop(null)
          }}
        >
          <form onSubmit={submitShopForm} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Trading Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="trading_name"
                  value={shopForm.trading_name}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Shop Number <span className="text-red-500">*</span>
                </label>
                <input
                  name="shop_number"
                  value={shopForm.shop_number}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Type</label>
                <select
                  name="business_type"
                  value={shopForm.business_type}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Owner (Customer) <span className="text-red-500">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  className="w-full text-sm"
                  options={ownerOptions}
                  isClearable
                  isSearchable
                  placeholder="Select owner"
                  value={ownerOptions.find(opt => opt.value === shopForm.owner_id) || null}
                  onChange={(option) => {
                    setShopForm(prev => ({
                      ...prev,
                      owner_id: option ? option.value : ''
                    }))
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#D1D5DB',
                      borderRadius: '0.5rem',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#D1D5DB'
                      }
                    })
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Location</label>
                <input
                  name="location"
                  value={shopForm.location}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Address / Landmark</label>
                <input
                  name="address"
                  value={shopForm.address}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Annual Rate (ZMW)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  name="monthly_rent"
                  value={shopForm.monthly_rent}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <select
                  name="status"
                  value={shopForm.status}
                  onChange={handleShopFormChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
              <textarea
                name="notes"
                value={shopForm.notes}
                onChange={handleShopFormChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Special conditions, agreements, etc"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShopModalOpen(false)
                  setEditingShop(null)
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingShop}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {savingShop ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FaPlus />
                    <span>{editingShop ? 'Update Shop' : 'Add Shop'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {billingModalOpen && selectedShop && (
        <Modal
          title={`Bill ${selectedShop.trading_name}`}
          onClose={() => {
            setBillingModalOpen(false)
            setSelectedShop(null)
          }}
        >
          <form onSubmit={submitBillingForm} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Amount (ZMW) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  name="amount"
                  value={billingForm.amount}
                  onChange={handleBillingChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Billing Period</label>
                <input
                  name="billing_period"
                  value={billingForm.billing_period}
                  onChange={handleBillingChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g. Jan 2025"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={billingForm.description}
                onChange={handleBillingChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Bill Date</label>
                <input
                  type="date"
                  name="bill_date"
                  value={billingForm.bill_date}
                  onChange={handleBillingChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Due Date</label>
                <input
                  type="date"
                  name="due_date"
                  value={billingForm.due_date}
                  onChange={handleBillingChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Collection Center <span className="text-red-500">*</span>
                </label>
                <select
                  name="cc_id"
                  value={billingForm.cc_id}
                  onChange={handleBillingChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  required
                >
                  <option value="">Select collection center</option>
                  {allData?.cc?.map((cc) => (
                    <option key={cc.id} value={cc.id}>{cc.collection_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Chart of Account <span className="text-red-500">*</span>
                </label>
                <select
                  name="coa_id"
                  value={billingForm.coa_id}
                  onChange={handleBillingChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                  required
                >
                  <option value="">Select COA</option>
                  {allData?.budget?.map((budget) => {
                    const coaDetails = allData?.coa?.find((c) => c.account == budget.coa)
                    const label = `${budget.coa} - ${coaDetails?.name ?? 'COA'}`
                    return (
                      <option key={budget.id} value={budget.id}>{label}</option>
                    )
                  })}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setBillingModalOpen(false)
                  setSelectedShop(null)
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingBill}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {savingBill ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Posting bill...</span>
                  </>
                ) : (
                  <>
                    <FaFileInvoiceDollar />
                    <span>Create Bill</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {importModalOpen && (
        <Modal
          title="Import Shops from Excel"
          onClose={closeImportModal}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Select Excel File (.xlsx, .xls, .csv)
              </label>
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <FaUpload className="w-10 h-10 mb-3 text-gray-400" />
                  <p className="mb-2 text-sm text-gray-600">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">Excel or CSV file</p>
                </div>
                <input
                  id="import-file-input"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImportFileChange}
                  className="hidden"
                />
              </label>
              {selectedFileName && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <span className="font-semibold">Selected:</span> {selectedFileName}
                  </p>
                </div>
              )}
            </div>

            {importRows.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-gray-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-600" />
                    <p className="text-sm font-bold text-gray-900">
                      Ready to Import: {importRows.length} shop{importRows.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="mt-3 max-h-40 overflow-y-auto bg-white rounded-lg border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="bg-gradient-to-r from-blue-950 to-blue-800 text-white sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Shop #</th>
                        <th className="px-3 py-2 text-left font-semibold">Trading Name</th>
                        <th className="px-3 py-2 text-left font-semibold">Owner</th>
                        <th className="px-3 py-2 text-left font-semibold">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.slice(0, 10).map((row, idx) => (
                        <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 font-medium">{row.shop_number}</td>
                          <td className="px-3 py-2">{row.trading_name}</td>
                          <td className="px-3 py-2">{row.owner_name}</td>
                          <td className="px-3 py-2">{row.business_type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importRows.length > 10 && (
                    <p className="mt-2 text-xs text-gray-600 text-center py-2 bg-gray-50">
                      ... and {importRows.length - 10} more
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeImportModal}
                disabled={importing}
                className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={importShops}
                disabled={importing || importRows.length === 0}
                className="flex-1 px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:shadow-lg transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Importing {importRows.length} shops...</span>
                  </>
                ) : (
                  <>
                    <FaUpload />
                    <span>Import {importRows.length} Shop{importRows.length !== 1 ? 's' : ''}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default Shops

